import { useState, useEffect } from 'react';
import {
  Menu,
  MenuTrigger,
  MenuButton,
  MenuPopover,
  MenuList,
  MenuItem,
  tokens,
} from '@fluentui/react-components';
import {
  ShieldTask20Regular,
  People20Regular,
  Person20Regular,
  Checkmark20Regular,
} from '@fluentui/react-icons';
import { VIEW_ROLE_STORAGE_KEY } from '../contexts/RoleContext';
import { readStoredValue, writeStoredValue } from '../lib/safeStorage';

type ViewRole = 'TenantAdmin' | 'Manager' | 'User';

interface RoleSwitcherProps {
  onRoleChange?: (role: ViewRole) => void;
}

const roleConfig = {
  TenantAdmin: {
    label: 'Tenant Admin',
    icon: ShieldTask20Regular,
    description: 'Full access to all features',
    color: tokens.colorPaletteRedBackground3,
  },
  Manager: {
    label: 'Manager',
    icon: People20Regular,
    description: 'Manage sites, lists, and contacts',
    color: tokens.colorPaletteBlueBorderActive,
  },
  User: {
    label: 'User',
    icon: Person20Regular,
    description: 'Standard user access',
    color: tokens.colorPaletteGreenBackground3,
  },
};

export function RoleSwitcher({ onRoleChange }: RoleSwitcherProps) {
  const [selectedRole, setSelectedRole] = useState<ViewRole>('TenantAdmin');

  useEffect(() => {
    // Load saved role preference from storage
    const savedRole = readStoredValue(VIEW_ROLE_STORAGE_KEY) as ViewRole | null;
    if (savedRole && roleConfig[savedRole]) {
      setSelectedRole(savedRole);
      onRoleChange?.(savedRole);
    }
  }, [onRoleChange]);

  const handleRoleChange = (role: ViewRole) => {
    setSelectedRole(role);
    writeStoredValue(VIEW_ROLE_STORAGE_KEY, role);
    onRoleChange?.(role);
  };

  const SelectedIcon = roleConfig[selectedRole].icon;

  return (
    <Menu positioning="above-start">
      <MenuTrigger disableButtonEnhancement>
        <MenuButton
          appearance="subtle"
          icon={<SelectedIcon />}
          size="small"
          style={{
            width: '100%',
            justifyContent: 'flex-start',
            color: tokens.colorNeutralForeground2,
            fontSize: tokens.fontSizeBase200,
          }}
        >
          View as: {roleConfig[selectedRole].label}
        </MenuButton>
      </MenuTrigger>

      <MenuPopover>
        <MenuList>
          {(Object.keys(roleConfig) as ViewRole[]).map((role) => {
            const RoleIcon = roleConfig[role].icon;
            const secondaryContent = selectedRole === role ? <Checkmark20Regular /> : null;
            return (
              <MenuItem
                key={role}
                icon={<RoleIcon />}
                secondaryContent={secondaryContent}
                onClick={() => handleRoleChange(role)}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div style={{ fontWeight: selectedRole === role ? 600 : 400 }}>
                    {roleConfig[role].label}
                  </div>
                  <div
                    style={{
                      fontSize: tokens.fontSizeBase200,
                      color: tokens.colorNeutralForeground3,
                    }}
                  >
                    {roleConfig[role].description}
                  </div>
                </div>
              </MenuItem>
            );
          })}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
